import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Skeleton } from "@/components/ui/feedback/Skeleton";
import { Spinner } from "@/components/ui/feedback/Spinner";
import type { LoadingStateProps } from "./LoadingState.types";

/* Exact compiled output of the pre-migration Tailwind utilities. The `&&`
 * overrides reproduce the old tailwind-merge results against the Spinner/
 * Skeleton primitives' own rules. */

const LoadingStateRoot = styled("div", {
  label: "AgenticLoadingState-root"
})({
  display: "flex",
  minHeight: "11rem",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: ".75rem",
  borderRadius: "var(--bfrs-radius-lg)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: "1.5rem",
  paddingRight: "1.5rem",
  paddingTop: "2rem",
  paddingBottom: "2rem",
  textAlign: "center"
});

const LoadingStateSpinner = styled(Spinner, {
  label: "AgenticLoadingState-spinner"
})({
  "&&": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
  }
});

const LoadingStateLabel = styled("div", {
  label: "AgenticLoadingState-label"
})({
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const LoadingStateDescription = styled("div", {
  label: "AgenticLoadingState-description"
})({
  marginTop: ".25rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const LoadingStateSkeletonStack = styled("div", {
  label: "AgenticLoadingState-skeletonStack"
})({
  width: "100%",
  maxWidth: "28rem",
  "& > :not([hidden]) ~ :not([hidden])": {
    "--tw-space-y-reverse": "0",
    marginTop: "calc(.75rem * calc(1 - var(--tw-space-y-reverse)))",
    marginBottom: "calc(.75rem * var(--tw-space-y-reverse))"
  }
});

const skeletonRowWidths: Record<number, CSSObject> = {
  0: { width: "83.333333%" },
  1: { width: "100%" },
  2: { width: "75%" }
};

const LoadingStateSkeletonRow = styled(Skeleton, {
  label: "AgenticLoadingState-skeletonRow",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { widthIndex: number } }>(({ ownerState }) => ({
  "&&": skeletonRowWidths[ownerState.widthIndex]
}));

export const LoadingState = forwardRef<HTMLDivElement, LoadingStateProps>(
  ({ className, label = "Loading", description, variant = "spinner", rows = 4, ...props }, ref) => (
    <LoadingStateRoot ref={ref} className={className} role="status" aria-live="polite" {...props}>
      {variant === "spinner" ? (
        <>
          <LoadingStateSpinner size="lg" label={typeof label === "string" ? label : "Loading"} />
          <div>
            <LoadingStateLabel>{label}</LoadingStateLabel>
            {description && <LoadingStateDescription>{description}</LoadingStateDescription>}
          </div>
        </>
      ) : (
        <LoadingStateSkeletonStack>
          {Array.from({ length: rows }).map((_, index) => (
            <LoadingStateSkeletonRow key={index} variant="text" ownerState={{ widthIndex: index % 3 }} />
          ))}
        </LoadingStateSkeletonStack>
      )}
    </LoadingStateRoot>
  )
);

LoadingState.displayName = "LoadingState";
